import { useEffect, useState } from 'react';

import { useVisualization } from '@/context/visualization-context';
import type { ViewportSlot } from '@/visualization/services/viewport-layout-service';
import type { ViewportType } from '@/visualization/viewports/types';

export interface ViewportSlotInfo {
    /** Slot config ID (e.g., "top-left", "3D-view", "svg-view") */
    id: string;
    /** Discriminant: 'viewport' for VTK cells, 'panel' for pure-React cells */
    slotKind: 'viewport' | 'panel';
    /** Viewport type — only set for viewport slots */
    type: ViewportType | undefined;
    /** Panel identifier — only set for panel slots */
    panelId: string | undefined;
    /** Available slot containers for this cell */
    slots: Partial<Record<ViewportSlot, HTMLElement>>;
}

/**
 * Hook that tracks named slot containers for all cells in the current layout.
 *
 * Subscribes to ViewportLayoutService LAYOUT_CHANGED events and returns the
 * requested slot elements for each cell. This is the generic foundation
 * used by useViewportToolbars and PanelManager.
 *
 * @param requestedSlots - Which slots to resolve. Defaults to all known slots.
 *
 * @example
 * // Get toolbar slots for viewport cells
 * const viewports = useViewportSlots(['toolbar']);
 *
 * // Get panel slots for panel cells
 * const panels = useViewportSlots(['panel']);
 */
export function useViewportSlots(requestedSlots?: ViewportSlot[]): ViewportSlotInfo[] {
    const { getViewportLayoutService } = useVisualization();
    const [viewports, setViewports] = useState<ViewportSlotInfo[]>([]);

    // Serialize to a stable primitive so that inline array literals (e.g.
    // useViewportSlots(['toolbar'])) don't create a new reference on every
    // render and trigger an infinite effect loop.
    const slotsKey = requestedSlots?.join(',') ?? '';

    useEffect(() => {
        const layoutService = getViewportLayoutService();
        const slotsToFetch = (slotsKey ? slotsKey.split(',') : ['toolbar', 'panel']) as ViewportSlot[];

        const handleLayoutChanged = () => {
            const layout = layoutService.getCurrentLayout();
            if (!layout) {
                setViewports([]);
                return;
            }

            const infos: ViewportSlotInfo[] = layout.viewports.map((config) => {
                const slots: Partial<Record<ViewportSlot, HTMLElement>> = {};
                for (const slot of slotsToFetch) {
                    const el = layoutService.getSlot(config.id, slot);
                    if (el) slots[slot] = el;
                }
                return {
                    id: config.id,
                    slotKind: config.slotKind,
                    type: config.slotKind === 'viewport' ? config.type : undefined,
                    panelId: config.slotKind === 'panel' ? config.panelId : undefined,
                    slots,
                };
            });

            setViewports(infos);
        };

        const unsub = layoutService.subscribe(layoutService.EVENTS.LAYOUT_CHANGED, handleLayoutChanged);

        // Handle component mounted after layout was already applied
        handleLayoutChanged();

        return unsub;
    }, [getViewportLayoutService, slotsKey]);

    return viewports;
}
